import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { 
  Loader2, 
  ShieldCheck,
  ChevronDown,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { translateAuthError } from "@/utils/auth-errors";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import logoAsset from "@/assets/logo.png.asset.json";

const signupSchema = z.object({
  nome: z.string().min(3, "Nome muito curto"),
  email: z.string().email("E-mail inválido"),
  telefone: z.string().min(10, "Telefone inválido"),
  empresa: z.string().optional(),
  password: z.string().min(8, "A senha deve ter pelo menos 8 caracteres"),
  segmentos_interesse: z.array(z.string()).min(1, "Selecione pelo menos um segmento"),
  termos: z.boolean().refine(v => v === true, {
    message: "Você deve aceitar os termos e a política de privacidade",
  }),
});

type SignupFormValues = z.infer<typeof signupSchema>;

export const Route = createFileRoute("/cadastro")({
  component: Signup,
});

function Signup() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [segmentos, setSegmentos] = useState<{ id: string; nome: string }[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      segmentos_interesse: [],
    },
  });

  const selectedSegmentos = watch("segmentos_interesse");

  useEffect(() => {
    if (user) {
      navigate({ to: '/aguardando' });
    }

    async function fetchSegmentos() {
      const { data } = await supabase
        .from('segmentos')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');
      if (data) setSegmentos(data);
    }
    fetchSegmentos();
  }, [user, navigate]);

  const onSubmit = async (values: SignupFormValues) => {
    setLoading(true);
    setError(null);

    const { error: signupError, data: signupData } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        data: {
          nome: values.nome,
          telefone: values.telefone,
          empresa: values.empresa,
          segmentos_interesse: values.segmentos_interesse,
          termos_aceitos_em: new Date().toISOString(),
          termos_versao: '2026-08-08'
        },
      },
    });

    if (signupError) {
      setError(translateAuthError(signupError.message));
      setLoading(false);
      return;
    }

    // Force profile update if it was just created to ensure fields are synced
    // The trigger should do this, but being explicit helps avoid empty fields
    if (signupData.user) {
      await supabase.from('profiles').update({
        nome: values.nome,
        telefone: values.telefone,
        empresa: values.empresa ?? null,
        segmentos_interesse: values.segmentos_interesse
      }).eq('id', signupData.user.id);
    }

    toast.success("Cadastro realizado!");
    navigate({ to: '/aguardando' });
  };

  const toggleSegmento = (id: string) => {
    const current = [...selectedSegmentos];
    const index = current.indexOf(id);
    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(id);
    }
    setValue("segmentos_interesse", current, { shouldValidate: true });
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      <Helmet>
        <title>Criar meu acesso | Lista de Fornecedores Gopage</title>
        <meta name="description" content="Cadastre sua empresa e ganhe acesso ao diretório privado de fornecedores e atacadistas exclusivos para clientes Gopage." />
        <link rel="canonical" href="https://spark-smile-hub.lovable.app/cadastro" />
      </Helmet>
      
      {/* Painel Esquerdo */}
      <div className="flex md:flex-col justify-between p-6 md:p-12 bg-[#0C1A12] text-white w-full md:w-1/3 min-h-[120px] md:min-h-screen">
        <div>
          <Link to="/">
            <img src={logoAsset.url} alt="Gopage" className="h-10 w-10 md:h-12 md:w-12 rounded-xl mb-12" />
          </Link>
          
          <div className="hidden md:block">
            <h2 className="text-4xl font-bold mb-6 leading-tight">Os melhores fornecedores estão aqui.</h2>
            <p className="text-lg opacity-80 mb-8">
              Crie sua conta para acessar os contatos diretos de fornecedores e atacadistas de todo o Brasil. Exclusivo para clientes Gopage.
            </p>
            <div className="space-y-4">
              {[
                "Fornecedores com CNPJ conferido",
                "Contato direto por WhatsApp",
                "Filtro por segmento, cidade e polo atacadista"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#C6F764]" />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* Nova seção lateral para não clientes */}
            <div className="mt-10 pt-10 border-t border-white/10">
              <h3 className="text-[18px] font-semibold mb-2 text-white">Ainda não é cliente Gopage?</h3>
              <p className="text-[#D1D5DB] text-[15px] mb-4 leading-relaxed">
                Crie seu catálogo digital, venda pelo WhatsApp e ganhe acesso à lista completa de fornecedores.
              </p>
              <p className="text-[#C6F764] font-semibold mb-6">A partir de R$ 39,90 por mês</p>
              
              <Button 
                asChild 
                className="w-full h-12 bg-transparent border-2 border-[#C6F764] text-[#C6F764] hover:bg-[#C6F764] hover:text-[#0C1A12] transition-all rounded-xl font-semibold flex items-center justify-center gap-2 group shadow-none"
              >
                <a 
                  href="https://gopage.bio?utm_source=listafornecedores&utm_medium=cadastro-lateral&utm_campaign=trial"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Conhecer a Gopage
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
          </div>
        </div>
        <div className="hidden md:block text-sm opacity-40">© 2026 Gopage</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="max-w-xl w-full py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Criar meu acesso</h1>
            <p className="text-muted-foreground leading-relaxed">
              A lista é exclusiva para clientes Gopage. Crie sua conta com o mesmo e-mail da sua conta Gopage.
            </p>
          </div>

          <div className="bg-[#C6F764]/20 border border-[#C6F764]/30 p-5 rounded-2xl mb-8 flex gap-4 items-start">
            <div className="bg-[#C6F764] p-2 rounded-xl text-[#0C1A12] shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <p className="text-sm font-semibold text-[#0C1A12] leading-relaxed">
              Use o mesmo e-mail da sua conta Gopage. É por ele que confirmamos seu acesso — se o e-mail não bater, o cadastro não é liberado.
            </p>
          </div>

          {/* Card Mobile Compacto */}
          <div className="md:hidden bg-[#0C1A12] p-6 rounded-2xl mb-8 text-white">
            <h3 className="text-lg font-semibold mb-2">Ainda não é cliente Gopage?</h3>
            <p className="text-sm text-[#D1D5DB] mb-3">Crie seu catálogo e ganhe acesso à lista.</p>
            <p className="text-[#C6F764] font-semibold mb-4 text-sm">R$ 39,90/mês</p>
            <Button 
              asChild 
              className="w-full h-11 bg-transparent border-2 border-[#C6F764] text-[#C6F764] hover:bg-[#C6F764] hover:text-[#0C1A12] transition-all rounded-xl text-sm font-semibold flex items-center justify-center gap-2 shadow-none"
            >
              <a 
                href="https://gopage.bio?utm_source=listafornecedores&utm_medium=cadastro-mobile&utm_campaign=trial"
                target="_blank"
                rel="noopener noreferrer"
              >
                Conhecer a Gopage
                <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">Nome Completo*</label>
                <input 
                  {...register("nome")}
                  className={`w-full px-4 py-4 bg-[#F7F9F5] border ${errors.nome ? 'border-destructive' : 'border-border'} rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#C6F764] transition-all`}
                  placeholder="Seu nome"
                />
                {errors.nome && <span className="text-xs text-destructive font-medium">{errors.nome.message}</span>}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">Telefone/WhatsApp*</label>
                <input 
                  {...register("telefone")}
                  className={`w-full px-4 py-4 bg-[#F7F9F5] border ${errors.telefone ? 'border-destructive' : 'border-border'} rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#C6F764] transition-all`}
                  placeholder="(00) 00000-0000"
                />
                {errors.telefone && <span className="text-xs text-destructive font-medium">{errors.telefone.message}</span>}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">E-mail*</label>
              <input 
                {...register("email")}
                type="email"
                className={`w-full px-4 py-4 bg-[#F7F9F5] border ${errors.email ? 'border-destructive' : 'border-border'} rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#C6F764] transition-all`}
                placeholder="mesmo-email-da-gopage@email.com"
              />
              {errors.email && <span className="text-xs text-destructive font-medium">{errors.email.message}</span>}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">Nome da Empresa</label>
              <input 
                {...register("empresa")}
                className="w-full px-4 py-4 bg-[#F7F9F5] border border-border rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#C6F764] transition-all"
                placeholder="Sua loja ou empresa"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">Senha* (mín. 8 caracteres)</label>
              <div className="relative">
                <input 
                  {...register("password")}
                  type={showPassword ? "text" : "password"}
                  className={`w-full px-4 py-4 bg-[#F7F9F5] border ${errors.password ? 'border-destructive' : 'border-border'} rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#C6F764] transition-all`}
                  placeholder="••••••••"
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#0C1A12]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Indicador de força da senha */}
              <div className="mt-2 space-y-2">
                <div className="flex gap-1 h-1">
                  {[1, 2, 3].map((step) => {
                    const password = watch("password") || "";
                    let strength = 0;
                    if (password.length >= 8) strength = 1;
                    if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)) strength = 2;
                    if (password.length >= 12 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) strength = 3;
                    
                    let color = "bg-border";
                    if (strength >= step) {
                      if (strength === 1) color = "bg-destructive";
                      if (strength === 2) color = "bg-yellow-500";
                      if (strength === 3) color = "bg-[#C6F764]";
                    }
                    
                    return <div key={step} className={`flex-1 rounded-full transition-colors ${color}`} />;
                  })}
                </div>
                <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <span>Mínimo de 8 caracteres</span>
                  <span className="text-right">
                    {(() => {
                      const password = watch("password") || "";
                      if (!password) return "";
                      if (password.length < 8) return "Fraca";
                      return "Média"; // Password has at least 8 characters, satisfies minimum requirement
                    })()}
                  </span>
                </div>
              </div>
              
              {errors.password && <span className="text-xs text-destructive font-medium">{errors.password.message}</span>}
            </div>

            <div className="space-y-3">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">Segmentos de interesse*</label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-4 bg-[#F7F9F5] border border-border rounded-2xl">
                {segmentos.map(seg => (
                  <button
                    key={seg.id}
                    type="button"
                    onClick={() => toggleSegmento(seg.id)}
                    className={`text-left px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                      selectedSegmentos.includes(seg.id) 
                        ? 'bg-[#0C1A12] text-white' 
                        : 'bg-white border border-border text-muted-foreground hover:border-[#C6F764]'
                    }`}
                  >
                    {seg.nome}
                  </button>
                ))}
              </div>
              {errors.segmentos_interesse && <span className="text-xs text-destructive font-medium">{errors.segmentos_interesse.message}</span>}
            </div>

            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input 
                  type="checkbox"
                  {...register("termos")}
                  className="mt-1 w-5 h-5 rounded-lg border-border text-[#C6F764] focus:ring-[#C6F764] cursor-pointer"
                  required
                />
                <span className="text-sm text-muted-foreground group-hover:text-[#0C1A12] transition-colors">
                  Li e concordo com os <Link to="/termos-de-uso" target="_blank" className="text-[#0C1A12] font-bold hover:underline">Termos de Uso</Link> e com a <Link to="/politica-de-privacidade" target="_blank" className="text-[#0C1A12] font-bold hover:underline">Política de Privacidade</Link>.
                </span>
              </label>
              {errors.termos && <span className="text-xs text-destructive font-medium block">{errors.termos.message}</span>}
            </div>

            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-2xl text-destructive text-sm font-medium">
                {error}
              </div>
            )}

            <Button 
              disabled={loading}
              variant="onLight"
              className="w-full py-7 font-bold rounded-2xl transition-all shadow-lg shadow-[#C6F764]/20 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Criar meu acesso"}
            </Button>
          </form>

          <div className="mt-8 space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Já tem uma conta? <Link to="/entrar" className="text-[#0C1A12] font-bold hover:underline">Entrar agora</Link>
            </p>
            
            <p className="text-center text-sm text-[#6B7280]">
              Ainda não é cliente Gopage? <a href="https://gopage.bio?utm_source=listafornecedores&utm_medium=cadastro-rodape&utm_campaign=trial" target="_blank" rel="noopener noreferrer" className="underline">Conhecer a plataforma</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
